import React from 'react';
import { ShipCustomization } from './types';

interface ShipCanvasProps {
  customization: ShipCustomization;
}

export const ShipCanvas: React.FC<ShipCanvasProps> = ({ customization }) => {
  const getFigureheadPath = () => {
    const figX = 50;
    const figY = 145;

    switch (customization.figurehead) {
      case 'skull':
        return (
          <g key="figurehead-skull">
            <circle cx={figX} cy={figY} r="28" fill="#f0f0f0" stroke="#333" strokeWidth="3" />
            <circle cx={figX - 12} cy={figY - 12} r="6" fill="#333" />
            <circle cx={figX + 12} cy={figY - 12} r="6" fill="#333" />
            <path d={`M ${figX - 8} ${figY + 2} L ${figX + 8} ${figY + 2}`} stroke="#333" strokeWidth="2.5" />
            <path d={`M ${figX} ${figY + 8} L ${figX - 8} ${figY + 18} L ${figX + 8} ${figY + 18} Z`} fill="#333" />
          </g>
        );
      case 'dragon':
        return (
          <g key="figurehead-dragon">
            {/* Dragon head - side profile (facing left) */}
            <ellipse cx={figX + 4} cy={figY} rx="24" ry="22" fill="#c41e3a" stroke="#8b0000" strokeWidth="3" />
            {/* Snout/jaw */}
            <path d={`M ${figX - 20} ${figY + 4} Q ${figX - 36} ${figY + 6} ${figX - 40} ${figY}`} fill="#a01828" stroke="#8b0000" strokeWidth="2.5" />
            {/* Horns */}
            <path d={`M ${figX + 10} ${figY - 20} L ${figX + 16} ${figY - 34}`} stroke="#8b0000" strokeWidth="4" fill="none" />
            <path d={`M ${figX - 6} ${figY - 22} L ${figX - 14} ${figY - 36}`} stroke="#8b0000" strokeWidth="4" fill="none" />
            {/* Horn tips */}
            <circle cx={figX + 16} cy={figY - 34} r="3" fill="#c41e3a" stroke="#8b0000" strokeWidth="2" />
            <circle cx={figX - 14} cy={figY - 36} r="3" fill="#c41e3a" stroke="#8b0000" strokeWidth="2" />
            {/* Eye */}
            <circle cx={figX - 4} cy={figY - 8} r="4" fill="#FFD700" />
            <circle cx={figX - 5} cy={figY - 9} r="1.6" fill="#000" />
            {/* Nostril */}
            <circle cx={figX - 34} cy={figY + 2} r="2" fill="#000" opacity="0.7" />
            {/* Scales on neck - slight ridge */}
            <path d={`M ${figX + 28} ${figY - 4} Q ${figX + 30} ${figY + 8} ${figX + 28} ${figY + 20}`} stroke="#8b0000" strokeWidth="2" fill="none" opacity="0.6" />
            {/* Lower jaw detail */}
            <path d={`M ${figX - 20} ${figY + 10} Q ${figX - 36} ${figY + 8} ${figX - 40} ${figY + 6}`} stroke="#8b0000" strokeWidth="2.5" fill="none" />
          </g>
        );
      case 'crown':
        return (
          <g key="figurehead-crown">
            <rect x={figX - 20} y={figY - 4} width="40" height="20" fill="#ffd700" stroke="#daa520" strokeWidth="3" />
            <polygon points={`${figX - 16},${figY - 4} ${figX - 10},${figY - 18} ${figX - 4},${figY - 4} ${figX + 4},${figY - 18} ${figX + 10},${figY - 4} ${figX + 16},${figY - 18} ${figX + 20},${figY - 4}`} fill="#ffd700" stroke="#daa520" strokeWidth="2.5" />
          </g>
        );
      case 'anchor':
        {
          const anchorX = 85;
          const anchorY = 215;
          return (
            <g key="figurehead-anchor">
              <line x1={anchorX} y1={anchorY - 20} x2={anchorX} y2={anchorY + 20} stroke="#666" strokeWidth="5" />
              <path d={`M ${anchorX - 20} ${anchorY + 20} Q ${anchorX} ${anchorY + 36} ${anchorX + 20} ${anchorY + 20}`} fill="none" stroke="#666" strokeWidth="5" />
              <circle cx={anchorX - 20} cy={anchorY + 20} r="6" fill="#666" />
              <circle cx={anchorX + 20} cy={anchorY + 20} r="6" fill="#666" />
            </g>
          );
        }
    }
  };

  const getSailPattern = () => {
    const baseId = Math.random();

    switch (customization.sailPattern) {
      case 'skull':
        return (
          <>
            <defs>
              <pattern id={`skull-pattern-${baseId}`} patternUnits="objectBoundingBox" width="1" height="1">
                <rect width="100" height="100" fill={customization.sailColor} />
                <circle cx="50" cy="35" r="12" fill="black" opacity="0.8" />
                <circle cx="42" cy="30" r="2" fill="white" />
                <circle cx="58" cy="30" r="2" fill="white" />
                <path d="M 48 38 L 52 38 M 47 42 L 48 45 M 53 42 L 52 45" stroke="black" strokeWidth="1" opacity="0.8" />
              </pattern>
            </defs>
            <polygon points="110,10 180,30 180,155 110,155" fill={`url(#skull-pattern-${baseId})`} />
            <polygon points="190,5 260,25 260,160 190,160" fill={`url(#skull-pattern-${baseId})`} />
          </>
        );
      case 'striped':
        return (
          <>
            <defs>
              <pattern id={`stripe-pattern-${baseId}`} patternUnits="objectBoundingBox" width="0.2" height="1">
                <rect width="10" height="100" fill={customization.sailColor} />
                <rect x="10" width="10" height="100" fill="white" opacity="0.6" />
              </pattern>
            </defs>
            <polygon points="110,10 180,30 180,155 110,155" fill={`url(#stripe-pattern-${baseId})`} />
            <polygon points="190,5 260,25 260,160 190,160" fill={`url(#stripe-pattern-${baseId})`} />
          </>
        );
      case 'plain':
      default:
        return (
          <>
            <polygon points="110,10 180,30 180,155 110,155" fill={customization.sailColor} />
            <polygon points="190,5 260,25 260,160 190,160" fill={customization.sailColor} />
          </>
        );
    }
  };

  const getFlagPattern = () => {
    const flagX = 255;
    const flagY = 50;
    const flagW = 40;
    const flagH = 30;

    switch (customization.flagPattern) {
      case 'jollyroger':
        return (
          <g key="flag-jollyroger">
            <rect x={flagX} y={flagY} width={flagW} height={flagH} fill={customization.flagColor} stroke="#000" strokeWidth="1.5" />

            {/* Skull */}
            <circle cx={flagX + 20} cy={flagY + 10} r="5.5" fill="white" opacity="0.95" />
            {/* Eye sockets */}
            <circle cx={flagX + 17.5} cy={flagY + 8} r="1.5" fill="#000" />
            <circle cx={flagX + 22.5} cy={flagY + 8} r="1.5" fill="#000" />
            {/* Nose hole */}
            <path d={`M ${flagX + 20} ${flagY + 11} L ${flagX + 19.5} ${flagY + 12} L ${flagX + 20.5} ${flagY + 12} Z`} fill="#000" />
            {/* Teeth */}
            <line x1={flagX + 16.5} y1={flagY + 14} x2={flagX + 23.5} y2={flagY + 14} stroke="#000" strokeWidth="0.8" />
            <line x1={flagX + 16.8} y1={flagY + 14} x2={flagX + 16.8} y2={flagY + 15} stroke="#000" strokeWidth="0.6" />
            <line x1={flagX + 17.8} y1={flagY + 14} x2={flagX + 17.8} y2={flagY + 15} stroke="#000" strokeWidth="0.6" />
            <line x1={flagX + 18.8} y1={flagY + 14} x2={flagX + 18.8} y2={flagY + 15} stroke="#000" strokeWidth="0.6" />
            <line x1={flagX + 19.8} y1={flagY + 14} x2={flagX + 19.8} y2={flagY + 15} stroke="#000" strokeWidth="0.6" />
            <line x1={flagX + 20.8} y1={flagY + 14} x2={flagX + 20.8} y2={flagY + 15} stroke="#000" strokeWidth="0.6" />
            <line x1={flagX + 21.8} y1={flagY + 14} x2={flagX + 21.8} y2={flagY + 15} stroke="#000" strokeWidth="0.6" />
            <line x1={flagX + 22.8} y1={flagY + 14} x2={flagX + 22.8} y2={flagY + 15} stroke="#000" strokeWidth="0.6" />

            {/* Crossbones behind skull */}
            <line x1={flagX + 10} y1={flagY + 20} x2={flagX + 30} y2={flagY + 8} stroke="white" strokeWidth="1.8" opacity="0.95" />
            <line x1={flagX + 30} y1={flagY + 20} x2={flagX + 10} y2={flagY + 8} stroke="white" strokeWidth="1.8" opacity="0.95" />
            {/* Bone ends */}
            <circle cx={flagX + 10} cy={flagY + 20} r="1.2" fill="white" opacity="0.95" />
            <circle cx={flagX + 30} cy={flagY + 20} r="1.2" fill="white" opacity="0.95" />
            <circle cx={flagX + 30} cy={flagY + 8} r="1.2" fill="white" opacity="0.95" />
            <circle cx={flagX + 10} cy={flagY + 8} r="1.2" fill="white" opacity="0.95" />
          </g>
        );
      case 'crossbones':
        return (
          <g key="flag-crossbones">
            <rect x={flagX} y={flagY} width={flagW} height={flagH} fill={customization.flagColor} stroke="#000" strokeWidth="1.5" />
            <line x1={flagX + 8} y1={flagY + 8} x2={flagX + 32} y2={flagY + 22} stroke="white" strokeWidth="1.5" opacity="0.9" />
            <line x1={flagX + 32} y1={flagY + 8} x2={flagX + 8} y2={flagY + 22} stroke="white" strokeWidth="1.5" opacity="0.9" />
            <circle cx={flagX + 20} cy={flagY + 15} r="2" fill="white" opacity="0.9" />
          </g>
        );
      case 'stripes':
      default:
        return (
          <g key="flag-stripes">
            <rect x={flagX} y={flagY} width={flagW} height={flagH} fill={customization.flagColor} stroke="#000" strokeWidth="1.5" />

            {/* Flames - orange and yellow fire */}
            {/* Left flame */}
            <path d={`M ${flagX + 10} ${flagY + 22} Q ${flagX + 8} ${flagY + 12} ${flagX + 12} ${flagY + 5}`} fill="#FF6B35" stroke="none" />
            <path d={`M ${flagX + 12} ${flagY + 22} Q ${flagX + 10} ${flagY + 10} ${flagX + 14} ${flagY + 3}`} fill="#FFA500" stroke="none" />

            {/* Center/right flame */}
            <path d={`M ${flagX + 18} ${flagY + 22} Q ${flagX + 16} ${flagY + 10} ${flagX + 20} ${flagY + 3}`} fill="#FF6B35" stroke="none" />
            <path d={`M ${flagX + 20} ${flagY + 22} Q ${flagX + 18} ${flagY + 8} ${flagX + 22} ${flagY + 2}`} fill="#FFD700" stroke="none" />

            {/* Right flame */}
            <path d={`M ${flagX + 26} ${flagY + 22} Q ${flagX + 24} ${flagY + 12} ${flagX + 28} ${flagY + 5}`} fill="#FF6B35" stroke="none" />
            <path d={`M ${flagX + 28} ${flagY + 22} Q ${flagX + 26} ${flagY + 10} ${flagX + 30} ${flagY + 3}`} fill="#FFA500" stroke="none" />

            {/* Glow effect - subtle white in center of flames */}
            <ellipse cx={flagX + 20} cy={flagY + 15} rx="3" ry="4" fill="white" opacity="0.3" />
          </g>
        );
    }
  };

  return (
    <svg width="400" height="300" viewBox="0 0 400 300" className="ship-canvas" style={{ border: '2px solid #333', borderRadius: '8px', backgroundColor: '#e6f2ff' }}>
      {/* Sky gradient effect */}
      <defs>
        <linearGradient id="skyGradient" x1="0%" y1="0%" x2="0%" y2="100%">
          <stop offset="0%" style={{ stopColor: '#87CEEB', stopOpacity: 1 }} />
          <stop offset="100%" style={{ stopColor: '#e6f2ff', stopOpacity: 1 }} />
        </linearGradient>
      </defs>

      {/* Water */}
      <path d="M 0 200 Q 100 195 200 200 T 400 200 L 400 300 L 0 300 Z" fill="#1e90ff" opacity="0.7" />
      <path d="M 0 220 Q 100 215 200 220 T 400 220 L 400 300 L 0 300 Z" fill="#4169e1" opacity="0.6" />

      {/* Wave details */}
      <path d="M 0 210 Q 50 205 100 210 T 200 210 T 300 210 T 400 210" fill="none" stroke="rgba(255,255,255,0.4)" strokeWidth="1.5" />
      <path d="M 0 240 Q 50 235 100 240 T 200 240 T 300 240 T 400 240" fill="none" stroke="rgba(255,255,255,0.3)" strokeWidth="1.5" />

      {/* Hull - large semi-circle from side view */}
      <g key="hull">
        {customization.hullStyle === 'merchant' && (
          <>
            {/* Main hull body */}
            <path d="M 60 140 L 310 140 Q 315 140 315 145 L 315 200 Q 315 210 310 215 Q 185 240 60 215 Q 55 210 55 200 L 55 145 Q 55 140 60 140" fill={customization.hullColor} stroke="#333" strokeWidth="2.5" />
            {/* Hull top line for depth */}
            <line x1="60" y1="140" x2="310" y2="140" stroke="#555" strokeWidth="1.5" opacity="0.7" />
            {/* Water line */}
            <line x1="55" y1="200" x2="315" y2="200" stroke="#1a1a1a" strokeWidth="1" opacity="0.5" />
          </>
        )}
        {customization.hullStyle === 'galleon' && (
          <>
            {/* Raised castle sections */}
            <rect x="65" y="120" width="50" height="25" fill={customization.hullColor} stroke="#333" strokeWidth="2" />
            {/* Main hull */}
            <path d="M 60 145 L 310 145 Q 315 145 315 150 L 315 205 Q 315 215 310 220 Q 185 245 60 220 Q 55 215 55 205 L 55 150 Q 55 145 60 145" fill={customization.hullColor} stroke="#333" strokeWidth="2.5" />
            <line x1="60" y1="145" x2="310" y2="145" stroke="#555" strokeWidth="1.5" opacity="0.7" />
            <line x1="55" y1="205" x2="315" y2="205" stroke="#1a1a1a" strokeWidth="1" opacity="0.5" />
          </>
        )}
        {customization.hullStyle === 'brigantine' && (
          <>
            {/* Sleeker hull */}
            <path d="M 70 135 L 305 135 Q 315 135 315 145 L 315 205 Q 315 215 305 225 Q 185 235 70 225 Q 60 215 60 205 L 60 145 Q 60 135 70 135" fill={customization.hullColor} stroke="#333" strokeWidth="2.5" />
            <line x1="70" y1="135" x2="305" y2="135" stroke="#555" strokeWidth="1.5" opacity="0.7" />
            <line x1="60" y1="205" x2="315" y2="205" stroke="#1a1a1a" strokeWidth="1" opacity="0.5" />
          </>
        )}
      </g>

      {/* Figurehead at the bow */}
      {getFigureheadPath()}

      {/* Masts - extending from sails down to touch the top of hull */}
      <line x1="130" y1="40" x2="130" y2="140" stroke="#8B4513" strokeWidth="5" />
      <line x1="220" y1="35" x2="220" y2="140" stroke="#8B4513" strokeWidth="4.5" />

      {/* Sails - positioned on masts, above the deck */}
      {getSailPattern()}

      {/* Flag - positioned at top of right mast */}
      {getFlagPattern()}

      {/* Stick person on the right side of the deck */}
      <g key="stick-person">
        {/* Head */}
        <circle cx="260" cy="125" r="3" fill="#F5DEB3" stroke="#333" strokeWidth="0.8" />
        {/* Body */}
        <line x1="260" y1="128" x2="260" y2="138" stroke="#333" strokeWidth="1.5" />
        {/* Left arm */}
        <line x1="260" y1="130" x2="253" y2="135" stroke="#333" strokeWidth="1.5" />
        {/* Right arm */}
        <line x1="260" y1="130" x2="267" y2="135" stroke="#333" strokeWidth="1.5" />
        {/* Left leg */}
        <line x1="260" y1="138" x2="255" y2="148" stroke="#333" strokeWidth="1.5" />
        {/* Right leg */}
        <line x1="260" y1="138" x2="265" y2="148" stroke="#333" strokeWidth="1.5" />
      </g>
    </svg>
  );
};
